import { isEmpty, size } from "lodash";
import { observer } from "mobx-react";
import React from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { pushIfNotExists } from "@fidget/common/utils/arrayHelpers";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { useBooleanMap } from "../../models/useBooleanMap";
import { ListHookWithFilters } from "../../models/useSettingsList/types";
import { withFilters } from "../../models/useSettingsList/useSettingsList";
import { SettingsForm } from "../../settingsForm";
import { DeleteConfirmationToastOnError } from "../../settingsForms/deleteConfirmation/deleteConfirmation";
import { ElementFormActions } from "../../settingsForms/elementForm/actions/elementFormActions";
import { ElementForm } from "../../settingsForms/elementForm/elementForm/elementForm";
import { ElementFormCheckbox } from "../../settingsForms/elementForm/icons/elementFormCheckbox";
import { ElementFormDeleteIcon } from "../../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { ElementFormEditIcon } from "../../settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormExpandIcon } from "../../settingsForms/elementForm/icons/elementFormExpandIcon";
import { ElementFormSaveDiscardIcon } from "../../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { BooleanListFilter } from "../../settingsForms/filters/booleanListFilter";
import { ListFilterWrapper } from "../../settingsForms/filters/listFilterWrapper/listFilterWrapper";
import { FilterWrapperProvider } from "../../settingsForms/filters/listFilterWrapper/useFilterWrapper";
import { TagFilter } from "../../settingsForms/filters/pickerFilters/tagFilter";
import { FormPlaceholder } from "../../settingsForms/fullWidthPlaceholder/formPlaceholder";
import {
  ListTagAction,
  ListHoursAction,
  ListBooleanAction,
  ListDeleteAction,
  ListActions,
  ListActionsSection
} from "../../listActions";
import { VerticalSeparator } from "../../settingsForms/separators/verticalSeparator/verticalSeparator";
import { ExistingGroupBox } from "./components/existingGroupBox/existingGroupBox";
import { NewTagGroupBox } from "./components/newTagGroupBox/newTagGroupBox";
import { ExistingTagGroupFormType, NewTagGroupFormType } from "./types";
import { TagGroupsProviderValue, useTagGroupsSettings } from "./useTagGroupsSettings";

type HookFilterable = ListHookWithFilters<
  ExistingTagGroupFormType,
  NewTagGroupFormType,
  TagGroupsProviderValue
>;

export const TagGroupsSettingsList = observer(() => {
  const hook: HookFilterable = withFilters(useTagGroupsSettings());
  const { items: expandedItems, setItem: setExpanded } = useBooleanMap();

  const {
    newItem,
    handleCreateNewClick,
    discardNewItem,
    saveNewItem,
    items,
    editedItems,
    filteredItems,
    forEachSelected,
    forAllSelected,
    setItemSelected,
    setItemsSelected,
    selectedItems,
    onExistingEditClick,

    onSubmitAll,
    onDiscardAll,
    apiDeleteItem,
    uiRemoveExistingItem,
    deleteAllSelected
  } = hook;

  const renderNewTagSection = () =>
    newItem ? (
      <ElementForm isEdited>
        <NewTagGroupBox form={newItem} />
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormSaveDiscardIcon onSave={saveNewItem} onDiscard={discardNewItem} />
        </ElementFormActions>
      </ElementForm>
    ) : (
      <FormPlaceholder
        onClick={handleCreateNewClick}
        text={"+ " + intl.formatMessage("settings.tagGroups.form.placeholder")}
      />
    );

  const renderElementForm = (item: ExistingTagGroupFormType, onDeleteClick: () => void) => {
    const isExpanded = !!expandedItems[item.$.id.$];

    return (
      <ElementForm>
        <ElementFormActions>
          <ElementFormCheckbox
            id={item.$.id.$}
            isSelected={!!selectedItems[item.$.id.$]}
            onChange={(val) => setItemSelected(item, val)}
          />
          <ElementFormExpandIcon onClick={() => setExpanded(item.$.id.$)} isExpanded={isExpanded} />
        </ElementFormActions>
        <VerticalSeparator />
        <ExistingGroupBox form={item} isExpanded={isExpanded} />
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormEditIcon onClick={() => onExistingEditClick(item)} />
          <ElementFormDeleteIcon onClick={onDeleteClick} />
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderExistingTagFormTypes = () => (
    <div>
      {filteredItems.map((item) => {
        return (
          <DeleteConfirmationToastOnError
            key={item.$.id.$}
            areYouSureText={intl.formatMessage("settings.groups.areYouSureToRemove")}
            succesfullyDeletedText={intl.formatMessage("settings.groups.removeConfirmation")}
            onDelete={() => apiDeleteItem(item.$.id.$)}
            onTimeoutPassed={() => uiRemoveExistingItem(item.$.id.$)}
            renderComponent={(onDeleteClick) => renderElementForm(item, onDeleteClick)}
          />
        );
      })}
    </div>
  );

  const renderFilterSection = () => {
    return (
      <FilterWrapperProvider<ExistingTagGroupFormType>>
        <ListFilterWrapper<ExistingTagGroupFormType>
          placeholder={intl.formatMessage("settings.tagGroups.filter.placeholder")}
          allCount={items.length}
          filteredCount={filteredItems.length}
          inputPredicateId="TagGroupInputFilter"
          inputPredicate={(item, input) => caseInsensitiveIncludes(item.$.name.$, input)}
          headerText={intl.formatMessage("settings.admin.tagGroups")}
        >
          <BooleanListFilter<ExistingTagGroupFormType>
            description={intl.formatMessage("settings.tags.filters.active")}
            filterId="TagGroupActive"
            predicate={(item) => item.$.active.$}
          />
          <BooleanListFilter<ExistingTagGroupFormType>
            description={intl.formatMessage("settings.tags.filters.showInDetails")}
            filterId="ShowInDetails"
            predicate={(item) => item.$.showInConversationDetails.$}
          />
          <TagFilter<ExistingTagGroupFormType>
            predicate={(tag) => (item) => item.$.tagIds.$.includes(tag.id)}
          />
        </ListFilterWrapper>
      </FilterWrapperProvider>
    );
  };

  const renderActionSection = () => {
    const areAllSelected = !!filteredItems.length && size(selectedItems) === size(filteredItems);

    return (
      <SettingsForm
        disabled={isEmpty(editedItems)}
        onDiscard={onDiscardAll}
        onSubmit={onSubmitAll}
        displayInline
      >
        <ListActions
          isChecked={areAllSelected}
          onCheckboxChanged={(val) => setItemsSelected(filteredItems, val)}
          isShowingActions={!isEmpty(selectedItems)}
        >
          <VerticalSeparator style="margin-halfLeft" />
          <ListActionsSection>
            <ListBooleanAction
              text={intl.formatMessage("settings.tags.tag.enabled")}
              isActive={forAllSelected((item) => item.$.active.$)}
              onClick={(val) => forEachSelected((i) => i.$.active.reset(val))}
            />

            <ListBooleanAction
              text={intl.formatMessage("settings.tags.tag.showInDetails")}
              isActive={forAllSelected((item) => item.$.showInConversationDetails.$)}
              onClick={(val) => forEachSelected((i) => i.$.showInConversationDetails.reset(val))}
            />
          </ListActionsSection>
          <VerticalSeparator />
          <ListActionsSection>
            <ListTagAction
              text={intl.formatMessage("settings.tagGroups.action.addTag")}
              onPicked={(tagId) =>
                forEachSelected((i) => i.$.tagIds.onChange(pushIfNotExists(i.$.tagIds.$, tagId)))
              }
            />
            <ListTagAction
              text={intl.formatMessage("settings.tagGroups.action.removeTag")}
              onPicked={(tagId) =>
                forEachSelected((i) => i.$.tagIds.onChange(i.$.tagIds.$.filter((v) => tagId !== v)))
              }
            />
            <ListHoursAction
              onPicked={(hours) => forEachSelected((i) => i.$.activeHours.onChange(hours))}
            />
          </ListActionsSection>
          <VerticalSeparator />
          <ListDeleteAction onDelete={deleteAllSelected} />
        </ListActions>
      </SettingsForm>
    );
  };

  const renderGroupListScreen = () => (
    <>
      {renderNewTagSection()}
      {renderFilterSection()}
      {!isEmpty(filteredItems) && renderActionSection()}
      {renderExistingTagFormTypes()}
    </>
  );

  return renderGroupListScreen();
});
